import { useEffect, useState } from 'react'
import { Bug, Lightbulb, MessageSquare, Loader2, Clock, CheckCircle2, XCircle, Wrench, Paperclip, ChevronDown } from 'lucide-react'
import api from '../../services/api'
import { useTheme } from '../../hooks/useTheme.jsx'

/**
 * What happened to the things I reported.
 *
 * Reports used to go in and never come back. The reporter saw a thank-you and then nothing —
 * not when it was picked up, not when it was fixed. From their side that is indistinguishable
 * from nobody having read it, which is why the same issue gets reported three times by three
 * different people, and why people eventually stop reporting at all.
 *
 * So this page exists to answer one question — "did anything happen?" — and it answers it in the
 * status chip, before any reading. The resolution note is the second half: "Resolved" with no
 * explanation is barely better than silence.
 */
export default function MyTickets() {
  const { isDark } = useTheme()
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)

  // Tickets assigned TO this person, kept separate from the ones they reported. Two different
  // relationships to a ticket — one is "I asked", the other is "I owe" — and merging them would
  // bury the second, which is the one with work attached.
  const [assigned, setAssigned] = useState([])
  const [busyId, setBusyId] = useState(null)
  // Which ticket is open. A list of titles told people THAT they had reported something, never WHAT
  // they had written -- the description, page and attachments were sent by the server and not shown.
  const [openId, setOpenId] = useState(null)
  const toggle = (id) => setOpenId(cur => (cur === id ? null : id))

  // Quietly empty for anybody who is never assigned anything — not an error worth showing.
  const loadAssigned = () =>
    api.get('/feedback/assigned-to-me')
      .then(({ data }) => setAssigned(data?.items || []))
      .catch(() => setAssigned([]))

  useEffect(() => {
    api.get('/feedback/mine')
      .then(({ data }) => setData(data))
      .catch(() => setError('Could not load your tickets.'))
    loadAssigned()
  }, [])

  // The assignee moves their OWN ticket along. The backend has always allowed this — being handed
  // a ticket is the whole point — but there was no button, so the email said "mark it In Progress"
  // and the screen offered no way to. Same endpoint the Admin inbox uses; the permission check
  // there already covers an assignee acting on what was given to them.
  const setStatus = async (id, status, note) => {
    setBusyId(id)
    try {
      const fd = new FormData()
      fd.append('status', status)
      if (note) fd.append('resolution_note', note)
      await api.patch(`/feedback/${id}/status`, fd)
      await loadAssigned()
    } catch (e) {
      alert(e.response?.data?.detail || 'Could not update this ticket. Please try again.')
    } finally {
      setBusyId(null)
    }
  }

  const card = isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
  const primary = isDark ? 'text-white' : 'text-gray-900'
  const secondary = isDark ? 'text-gray-400' : 'text-gray-500'

  // Status carries the whole message, so it gets a shape and a colour, not just a word.
  const STATUS = {
    'Open': { cls: 'bg-slate-100 text-slate-700', Icon: Clock, says: 'Received — not started yet' },
    'In Progress': { cls: 'bg-blue-100 text-blue-700', Icon: Wrench, says: 'Somebody is working on it' },
    'Resolved': { cls: 'bg-green-100 text-green-700', Icon: CheckCircle2, says: 'Fixed' },
    "Won't Fix": { cls: 'bg-gray-100 text-gray-600', Icon: XCircle, says: 'Closed without a change' },
  }
  const TYPE_ICON = { Bug: Bug, Suggestion: Lightbulb }

  const stamp = (iso) => (iso ? new Date(iso).toLocaleString() : '')

  // Everything about one ticket, in the order a reader asks: what did it say, where, who has it.
  const Details = ({ t }) => (
    <div className={`mt-3 rounded-lg border px-3 py-3 text-sm space-y-2 ${isDark ? 'bg-gray-900 border-gray-700 text-gray-300' : 'bg-gray-50 border-gray-200 text-gray-700'}`}>
      <div>
        <span className={`block text-[11px] font-medium mb-0.5 ${secondary}`}>What was reported</span>
        <span className="whitespace-pre-wrap">{t.description || 'No description was written.'}</span>
      </div>
      <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-xs">
        <dt className={secondary}>Type</dt><dd>{t.type || 'Feedback'}</dd>
        {t.page && (<><dt className={secondary}>On page</dt><dd className="break-all">{t.page}</dd></>)}
        <dt className={secondary}>Reported</dt><dd>{stamp(t.created_at)}{t.submitter_name ? ` by ${t.submitter_name}` : ''}</dd>
        <dt className={secondary}>Assigned to</dt><dd>{t.assignee_name || 'Not assigned yet'}</dd>
        {t.status_changed_at && (<><dt className={secondary}>Last change</dt><dd>{stamp(t.status_changed_at)}</dd></>)}
        {t.resolved_at && (<><dt className={secondary}>Closed</dt><dd>{stamp(t.resolved_at)}</dd></>)}
      </dl>
      {t.attachments?.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {t.attachments.map((a, i) => (
            <a key={i} href={a.url} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline">
              <Paperclip className="w-3 h-3" /> {a.name}
            </a>
          ))}
        </div>
      )}
    </div>
  )

  const when = (iso) => {
    if (!iso) return ''
    const d = new Date(iso)
    const days = Math.floor((Date.now() - d.getTime()) / 86400000)
    if (days === 0) return 'today'
    if (days === 1) return 'yesterday'
    if (days < 30) return `${days} days ago`
    return d.toLocaleDateString()
  }

  return (
    <div className={`min-h-full p-6 ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="max-w-3xl mx-auto space-y-5">

        <div>
          <h1 className={`text-2xl font-bold ${primary}`}>My tickets</h1>
          <p className={`text-sm mt-1 ${secondary}`}>
            Everything you have reported, and what happened to it.
          </p>
        </div>

        {/* Work handed to this person. Shown FIRST and only when there is some — these are the
            ones with something owed, where the reported list is mostly a record of the past. */}
        {assigned.length > 0 && (
          <div className={`border rounded-xl p-4 ${isDark ? 'bg-amber-900/20 border-amber-800/50' : 'bg-amber-50 border-amber-200'}`}>
            <h2 className={`text-sm font-bold ${isDark ? 'text-amber-200' : 'text-amber-900'}`}>
              Assigned to you
              <span className="ml-2 font-normal">
                {assigned.filter(t => t.status === 'Open' || t.status === 'In Progress').length} still open
              </span>
            </h2>
            <ul className="mt-3 space-y-2">
              {assigned.map(t => (
                <li key={t.id}
                    className={`flex items-start gap-3 rounded-lg border px-3 py-2 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-amber-200'}`}>
                  <div className="min-w-0 flex-1">
                    <button type="button" onClick={() => toggle(`a${t.id}`)} aria-expanded={openId === `a${t.id}`}
                            className="w-full text-left">
                      <p className={`text-sm font-medium ${openId === `a${t.id}` ? '' : 'truncate'} ${primary}`}>{t.title}</p>
                      <p className={`text-xs mt-0.5 flex items-center gap-1 ${secondary}`}>
                        <span className="truncate">{t.ticket_ref} &middot; reported by {t.submitter_name || 'someone'}
                        {t.page ? ` · ${t.page}` : ''}</span>
                        <ChevronDown className={`w-3 h-3 shrink-0 transition-transform ${openId === `a${t.id}` ? 'rotate-180' : ''}`} />
                      </p>
                    </button>
                    {openId === `a${t.id}` && <Details t={t} />}
                  </div>
                  <div className="shrink-0 flex flex-col items-end gap-1.5">
                    <span className={`text-[11px] font-semibold px-2 py-1 rounded-full ${
                      t.status === 'Resolved' ? 'bg-emerald-100 text-emerald-700'
                      : t.status === 'In Progress' ? 'bg-blue-100 text-blue-700'
                      : t.status === "Won't Fix" ? 'bg-gray-200 text-gray-600'
                      : 'bg-amber-100 text-amber-800'}`}>
                      {t.status}
                    </span>
                    {/* Only while there is still something to do. A closed ticket stays listed —
                        it is a record of what this person finished — but needs no buttons. */}
                    {t.status !== 'Resolved' && t.status !== "Won't Fix" && (
                      <div className="flex items-center gap-1.5">
                        {t.status === 'Open' && (
                          <button
                            onClick={() => setStatus(t.id, 'In Progress')}
                            disabled={busyId === t.id}
                            className="text-[11px] font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg px-2.5 py-1 disabled:opacity-50"
                          >
                            Pick up
                          </button>
                        )}
                        <button
                          onClick={() => {
                            // Asked here because this is the only moment anybody knows what was
                            // actually done, and it is sent verbatim to whoever reported it.
                            const note = window.prompt(
                              `Resolving ${t.ticket_ref}. What did you do? ` +
                              `(sent to ${t.submitter_name || 'the reporter'} — leave blank to skip)`)
                            if (note === null) return
                            setStatus(t.id, 'Resolved', note.trim())
                          }}
                          disabled={busyId === t.id}
                          className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg px-2.5 py-1 disabled:opacity-50"
                        >
                          Resolve
                        </button>
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {error && <p className="text-sm text-red-600">{error}</p>}
        {!data && !error && (
          <p className={`text-sm flex items-center gap-2 ${secondary}`}>
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </p>
        )}

        {data && data.items.length === 0 && (
          <div className={`rounded-xl border p-8 text-center ${card}`}>
            <p className={`text-sm ${secondary}`}>
              You have not reported anything yet. Use the feedback button in the top bar — bugs,
              suggestions, anything that looks wrong.
            </p>
          </div>
        )}

        {data && data.items.length > 0 && (
          <>
            {data.open_count > 0 && (
              <p className={`text-sm ${secondary}`}>
                {data.open_count} of your {data.items.length} still open.
              </p>
            )}

            <div className="space-y-3">
              {data.items.map(t => {
                const st = STATUS[t.status] || STATUS['Open']
                const TypeIcon = TYPE_ICON[t.type] || MessageSquare
                return (
                  <div key={t.id} className={`rounded-xl border p-4 ${card}`}>
                    <div className="flex items-start gap-3 cursor-pointer" onClick={() => toggle(`r${t.id}`)}
                         role="button" tabIndex={0} aria-expanded={openId === `r${t.id}`}
                         onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(`r${t.id}`) } }}>
                      <TypeIcon className={`w-4 h-4 mt-1 shrink-0 ${secondary}`} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          {/* The reference first, because it is what they quote when asking. */}
                          <span className={`font-mono text-xs ${secondary}`}>{t.ticket_ref}</span>
                          <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full ${st.cls}`}>
                            <st.Icon className="w-3 h-3" /> {t.status}
                          </span>
                        </div>
                        <p className={`font-medium mt-1 ${primary}`}>{t.title}</p>
                        <p className={`text-xs mt-0.5 ${secondary}`}>
                          {st.says} · reported {when(t.created_at)}
                          {t.resolved_at && ` · closed ${when(t.resolved_at)}`}
                        </p>

                        {/* The half that makes "Resolved" mean something. */}
                        {t.resolution_note && (
                          <div className={`mt-2 rounded-lg px-3 py-2 text-sm ${isDark
                            ? 'bg-gray-900 text-gray-300' : 'bg-gray-50 text-gray-700'}`}>
                            <span className={`block text-[11px] font-medium mb-0.5 ${secondary}`}>
                              What we did
                            </span>
                            <span className="whitespace-pre-line">{t.resolution_note}</span>
                          </div>
                        )}

                        {t.status === 'Resolved' && !t.resolution_note && (
                          <p className={`text-xs mt-1.5 ${secondary}`}>
                            Marked fixed. If you are still seeing it, report it again and say so —
                            that is more useful than it sounds.
                          </p>
                        )}

                        <span className={`inline-flex items-center gap-1 text-xs mt-2 ${isDark ? 'text-indigo-300' : 'text-indigo-600'}`}>
                          {openId === `r${t.id}` ? 'Hide details' : 'View details'}
                          <ChevronDown className={`w-3 h-3 transition-transform ${openId === `r${t.id}` ? 'rotate-180' : ''}`} />
                        </span>
                        {openId === `r${t.id}` && (
                          // A click inside the details (an attachment link, selecting text) must not close it.
                          <div onClick={(e) => e.stopPropagation()}><Details t={t} /></div>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
